import { useState, useEffect } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Trash2, Sparkles, ScanText, Plus, SlidersHorizontal, ShieldCheck, AlertTriangle } from "lucide-react";
import { useFields } from "@/context/FieldsContext";
import { resolvedEnabledFields } from "@/lib/fields";

const CONFIDENCE = {
  high: { label: "Verified", cls: "bg-accent/15 text-accent border-accent/30", Icon: ShieldCheck },
  medium: { label: "Double-check", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", Icon: AlertTriangle },
  low: { label: "Needs review", cls: "bg-destructive/15 text-destructive border-destructive/30", Icon: AlertTriangle },
};

export const AiReviewDrawer = ({ open, onOpenChange, stagedItems, onConfirm, onEditFields }) => {
  const { config } = useFields();
  const criteria = resolvedEnabledFields(config);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setRows(stagedItems || []);
  }, [stagedItems]);

  const update = (idx, field, value) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const updateField = (idx, fieldId, value) =>
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, fields: { ...(r.fields || {}), [fieldId]: value } } : r))
    );

  const discard = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const confirm = () => {
    const payload = rows.map((r) => ({
      item_name: r.item_name,
      description: r.description,
      category: r.category || "Other",
      quantity: Number(r.quantity) || 1,
      source_image_url: r.source_image_url || null,
      extracted_text: r.extracted_text || "",
      fields: r.fields || {},
    }));
    onConfirm(payload);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="ai-review-drawer"
        side="right"
        className="bg-card border-border w-full sm:max-w-xl p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-4 border-b border-border">
          <SheetTitle className="font-heading text-slate-50 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Review AI Results
          </SheetTitle>
          <p className="text-xs text-muted-foreground">
            {rows.length} item{rows.length === 1 ? "" : "s"} extracted · edit anything before adding to inventory.
          </p>
          {onEditFields && (
            <button
              data-testid="review-edit-fields-button"
              onClick={onEditFields}
              className="mt-1 inline-flex items-center gap-1.5 text-xs text-primary hover:underline w-fit"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" /> Edit fields & AI criteria
            </button>
          )}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {rows.length === 0 && (
            <div className="text-center py-16 text-muted-foreground text-sm">
              All items discarded. Close to cancel.
            </div>
          )}
          {rows.map((r, idx) => (
            <div
              key={r.staging_id || idx}
              className="rounded-xl border border-border bg-secondary/20 p-4 fade-up"
            >
              {(() => {
                const conf = CONFIDENCE[r.confidence] || CONFIDENCE.medium;
                return (
                  <div className="flex items-center justify-between mb-2.5">
                    <Badge
                      variant="outline"
                      data-testid="review-confidence-badge"
                      data-confidence={r.confidence || "medium"}
                      className={`text-[10px] font-medium gap-1 ${conf.cls}`}
                    >
                      <conf.Icon className="h-3 w-3" /> AI self-check: {conf.label}
                    </Badge>
                    {r.quantity > 1 && (
                      <span className="text-[10px] text-muted-foreground">Qty detected: {r.quantity}</span>
                    )}
                  </div>
                );
              })()}
              {r.review_note && (
                <div
                  data-testid="review-note"
                  className="mb-2.5 flex items-start gap-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 px-2.5 py-1.5 text-[11px] text-amber-200"
                >
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>{r.review_note}</span>
                </div>
              )}
              <div className="flex gap-3">
                <div className="shrink-0">
                  {r.preview ? (
                    <img src={r.preview} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" />
                  ) : (
                    <div className="h-16 w-16 rounded-lg border border-dashed border-border flex items-center justify-center">
                      <ScanText className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <Input
                    data-testid="edit-extracted-item-field"
                    value={r.item_name}
                    onChange={(e) => update(idx, "item_name", e.target.value)}
                    className="bg-card border-border font-semibold text-slate-100"
                    placeholder="Item name"
                  />
                  <Textarea
                    value={r.description}
                    onChange={(e) => update(idx, "description", e.target.value)}
                    rows={2}
                    className="bg-card border-border resize-none text-xs"
                    placeholder="Description / attributes"
                  />
                </div>
                <button
                  data-testid="discard-staging-item-button"
                  onClick={() => discard(idx)}
                  className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                <Input
                  value={r.category}
                  onChange={(e) => update(idx, "category", e.target.value)}
                  className="bg-card border-border text-xs"
                  placeholder="Category"
                />
                <Input
                  type="number"
                  min={0}
                  value={r.quantity}
                  onChange={(e) => update(idx, "quantity", e.target.value)}
                  className="bg-card border-border text-xs"
                  placeholder="Qty"
                />
              </div>

              {criteria.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2" data-testid="review-custom-fields">
                  {criteria.map((f) => (
                    <div key={f.id}>
                      <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {f.resolvedLabel}
                      </label>
                      <Input
                        data-testid="review-field-input"
                        value={(r.fields && r.fields[f.id]) || ""}
                        onChange={(e) => updateField(idx, f.id, e.target.value)}
                        className="bg-card border-border text-xs mt-0.5"
                        placeholder={f.ai_instruction || f.resolvedLabel}
                      />
                    </div>
                  ))}
                </div>
              )}

              {r.extracted_text ? (
                <div className="mt-3 rounded-lg bg-primary/10 border border-primary/20 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-primary mb-0.5">
                    <ScanText className="h-3 w-3" /> OCR / Extracted
                  </div>
                  <div className="font-mono text-[11px] text-sky-300 break-words">{r.extracted_text}</div>
                </div>
              ) : (
                <Badge variant="outline" className="mt-3 text-[10px] text-muted-foreground border-border">
                  Visual description (no text detected)
                </Badge>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center gap-3">
          <Button variant="ghost" className="flex-1" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="confirm-batch-add-button"
            onClick={confirm}
            disabled={rows.length === 0}
            className="flex-1 gap-2 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold disabled:opacity-40"
          >
            <CheckCircle2 className="h-4 w-4" /> Add {rows.length} to Inventory
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
